import { InvoiceAggregationTimeframe, PoRequirementType } from 'types/org';
import {
  InvoiceApprovalRules,
  UpdatePurchaseLinePricesFromBill,
} from 'types/suppliers';
import * as Yup from 'yup';

export const formConstants = {
  poFooterNotes: {
    name: 'poFooterNotes',
    label: 'Default PO Footer Text',
    description:
      'The default text that will be displayed on new PDF purchase orders generated in Cofactr',
  },
  invoiceApproval: {
    name: 'invoiceApproval',
    label: 'Supplier Bill Auto-Approval Rule',
    description:
      'This determines when Supplier Bills for this supplier will be auto-approved.',
  },
  updatePurchaseLinePricesFromBill: {
    name: 'updatePurchaseLinePricesFromBill',
    label: 'Update Purchase Line Prices from Bill',
    description:
      'If enabled, price discrepancies between the PO line and Supplier Bill line will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  onlyUseTotalForBillApproval: {
    name: 'onlyUseTotalForBillApproval',
    label: 'Only Use Total for Bill Approval',
    description:
      'If enabled, only the total price of the Supplier Bill will be used to determine if the PO should be auto-approved. Line-level matching will be ignored.',
  },
  ignoreShippingInBillApproval: {
    name: 'ignoreShippingInBillApproval',
    label: 'Ignore Shipping in Bill Approval',
    description:
      'If enabled, shipping price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseShippingFromBill: {
    name: 'updatePurchaseShippingFromBill',
    label: 'Update Purchase Shipping from Bill',
    description:
      'If enabled, shipping price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  ignoreDutiesInBillApproval: {
    name: 'ignoreDutiesInBillApproval',
    label: 'Ignore Duties in Bill Approval',
    description:
      'If enabled, duty price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseDutiesFromBill: {
    name: 'updatePurchaseDutiesFromBill',
    label: 'Update Purchase Duties from Bill',
    description:
      'If enabled, duty price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  ignoreTaxesInBillApproval: {
    name: 'ignoreTaxesInBillApproval',
    label: 'Ignore Taxes in Bill Approval',
    description:
      'If enabled, tax price discrepancies between the PO and Supplier Bill will be ignored in the bill approval process.',
  },
  updatePurchaseTaxesFromBill: {
    name: 'updatePurchaseTaxesFromBill',
    label: 'Update Purchase Taxes from Bill',
    description:
      'If enabled, tax price discrepancies between the PO and Supplier Bill will cause the PO to be automatically updated to match the Supplier Bill.',
  },
  requireAdminApprovals: {
    name: 'requireAdminApprovals',
    label: 'Require Admin Approvals',
    description:
      'If enabled, Purchase Orders, Requisitions, and Supplier Bills will only be able to be approved by admins.',
  },
  allowSelfManagedPurchasing: {
    name: 'allowSelfManagedPurchasing',
    label: 'Allow Self-Managed Purchasing',
    description:
      'If enabled, users will be able to add suppliers and initiate sourcing and purchasing that will be self-managed by your own in-house team directly with suppliers.',
  },
  allowCofactrManagedPurchasing: {
    name: 'allowCofactrManagedPurchasing',
    label: 'Allow Cofactr-Managed Purchasing',
    description:
      'If enabled, users will be able to add suppliers and initiate sourcing and purchasing that is managed by Cofactr on your behalf.',
  },
  allowableCofactrManagedEstimateOverage: {
    name: 'allowableCofactrManagedEstimateOverage',
    label: 'Allowable Cofactr-Managed Estimate Overage',
    description:
      'When initiating a Managed by Cofactr purchase, a system-generated estimate will be displayed for your users. In some cases, there may be a difference between the estimate and the actual costs. This field allows you to specify the percentage of the estimate that is allowed to be exceeded before the purchase is blocked pending additional approval.',
  },
  poRequirementType: {
    name: 'poRequirementType',
    label: 'PO Number Requirement Type',
    description:
      'This determines the rules for which PO number will be used for invoices generated by Cofactr for Managed by Cofactr orders.',
  },
  customPoPrefix: {
    name: 'customPoPrefix',
    label: 'Custom PO Prefix',
    description:
      'If a custom PO prefix is specified, it will be used as the prefix for all PO numbers generated for purchase orders.',
  },
  customPoNumberLength: {
    name: 'customPoNumberLength',
    label: 'Custom PO Number Length',
    description:
      'If a custom PO number length is specified, it will be used as the length for all PO numbers generated for purchase orders.',
  },
  poRequiredMessage: {
    name: 'poRequiredMessage',
    label: 'PO Number Required Message',
    description:
      'This message will be displayed to users when a PO number is required for a purchase and they have not entered one.',
  },
  autoApproveSupplierQuotes: {
    name: 'autoApproveSupplierQuotes',
    label: 'Auto-Approve Supplier Quotes',
    description:
      'If enabled, supplier quotes will be auto-approved when they are submitted.',
  },
  orderApprovalThreshold: {
    name: 'orderApprovalThreshold',
    label: 'Approval Threshold',
    description:
      'Purchase Orders and Supplier Bills below this threshold will be able to be approved by any user with Edit permissions to the relevant App. Above this threshold, they will require approval from an Admin for the app.',
  },
  aggregateInvoicesByPoNumber: {
    name: 'aggregateInvoicesByPoNumber',
    label: 'Aggregate Invoices by PO Number',
    description:
      'If enabled, all PO lines billed within a given timeframe will be aggregated into a single invoice based on the PO number you provide.',
  },
  aggregateInvoicesByRequisition: {
    name: 'aggregateInvoicesByRequisition',
    label: 'Aggregate Invoices by Cart',
    description:
      'If enabled, all PO lines billed within a given timeframe will be aggregated into a single invoice based on their associated Ordered Cart.',
  },
  aggregateInvoicesBySupplierOrder: {
    name: 'aggregateInvoicesBySupplierOrder',
    label: 'Aggregate Invoices by Supplier Order',
    description:
      'If enabled, all PO lines billed within a given timeframe will be aggregated into a single invoice based on their associated Supplier PO.',
  },
  aggregateInvoicesByTeam: {
    name: 'aggregateInvoicesByTeam',
    label: 'Aggregate Invoices by Team',
    description:
      'If enabled, all PO lines billed within a given timeframe will be aggregated into a single invoice based on their associated Team.',
  },
  aggregateInvoicesByProgram: {
    name: 'aggregateInvoicesByProgram',
    label: 'Aggregate Invoices by Program',
    description:
      'If enabled, all PO lines billed within a given timeframe will be aggregated into a single invoice based on their associated Program.',
  },
  aggregateInvoicesTimeframe: {
    name: 'aggregateInvoicesTimeframe',
    label: 'Aggregate Invoices Timeframe',
    description:
      'This controls how frequently your spend will be aggregated into invoice(s) and billed to you. Please contact Cofactr Success to request changes to this setting.',
  },
  blanketPartsPo: {
    name: 'blanketPartsPo',
    label: 'Blanket Parts PO',
    description:
      'If provided, all invoices from Cofactr for parts purchases will include this PO number unless a specific PO number is provided for the associated requisition or PO instead.',
  },
  blanketSubscriptionPo: {
    name: 'blanketSubscriptionPo',
    label: 'Blanket Inventory Services PO',
    description:
      'If provided, all invoices from Cofactr for storage and kitting services will include this PO number.',
  },
};

export const formValidation = Yup.object().shape({
  [formConstants.poFooterNotes.name]: Yup.string().nullable(),
  [formConstants.invoiceApproval.name]: Yup.string().oneOf(
    Object.values(InvoiceApprovalRules)
  ),
  [formConstants.updatePurchaseLinePricesFromBill.name]: Yup.string().oneOf(
    Object.values(UpdatePurchaseLinePricesFromBill)
  ),
  [formConstants.ignoreShippingInBillApproval.name]: Yup.boolean(),
  [formConstants.updatePurchaseShippingFromBill.name]: Yup.boolean(),
  [formConstants.ignoreDutiesInBillApproval.name]: Yup.boolean(),
  [formConstants.updatePurchaseDutiesFromBill.name]: Yup.boolean(),
  [formConstants.ignoreTaxesInBillApproval.name]: Yup.boolean(),
  [formConstants.updatePurchaseTaxesFromBill.name]: Yup.boolean(),
  [formConstants.onlyUseTotalForBillApproval.name]: Yup.boolean(),
  [formConstants.requireAdminApprovals.name]: Yup.boolean(),
  [formConstants.allowSelfManagedPurchasing.name]: Yup.boolean(),
  [formConstants.allowCofactrManagedPurchasing.name]: Yup.boolean(),
  [formConstants.allowableCofactrManagedEstimateOverage.name]: Yup.string(),
  [formConstants.poRequirementType.name]: Yup.string().oneOf(
    Object.values(PoRequirementType)
  ),
  [formConstants.customPoPrefix.name]: Yup.string()
    .max(5, 'Up to 5 characters')
    .nullable(),
  [formConstants.customPoNumberLength.name]: Yup.number().when(
    formConstants.customPoPrefix.name,
    {
      is: (prefix: string | null | undefined) => !prefix,
      then: (schema) => schema.min(0).max(7).nullable(),
      otherwise: (schema) => schema.min(4).max(7).nullable(),
    }
  ),
  [formConstants.poRequiredMessage.name]: Yup.string().nullable(),
  [formConstants.autoApproveSupplierQuotes.name]: Yup.boolean(),
  [formConstants.orderApprovalThreshold.name]: Yup.number().nullable(),
  [formConstants.aggregateInvoicesByPoNumber.name]: Yup.boolean(),
  [formConstants.aggregateInvoicesByRequisition.name]: Yup.boolean(),
  [formConstants.aggregateInvoicesBySupplierOrder.name]: Yup.boolean(),
  [formConstants.aggregateInvoicesByTeam.name]: Yup.boolean(),
  [formConstants.aggregateInvoicesByProgram.name]: Yup.boolean(),
  [formConstants.aggregateInvoicesTimeframe.name]: Yup.string().oneOf(
    Object.values(InvoiceAggregationTimeframe)
  ),
  [formConstants.blanketPartsPo.name]: Yup.string()
    .max(32, 'Up to 32 characters')
    .nullable(),
  [formConstants.blanketSubscriptionPo.name]: Yup.string()
    .max(32, 'Up to 32 characters')
    .nullable(),
});
